// @ts-strict-ignore
import { useEffect, useRef, useState } from 'react';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import { routes } from '~/helpers/routes';

// Per this article: https://stackoverflow.com/questions/3468607
// setTimeout's millisecond delay maxes out at the highest number possible in 32-bit computing
const SETTIMEOUT_MAXIMUM = 2147483647;

// Minimum delta (client's idleFor - server's response) to treat the server response as
// evidence of another tab's activity worth syncing to. Smaller deltas are just rounding
// echoes and would create a feedback loop that pins idleFor below the warn threshold.
const SERVER_SYNC_THRESHOLD_SECONDS = 1;

type UseIdleSessionProps = {
  /**
   * How frequently to throttle the trackActivity callback
   * @default 1_000 (1 second)
   */
  activityThrottleTimeMS?: number;

  /**
   * How many miliseconds before expiration a user should be marked as idle
   * @default 30_000 (30 seconds)
  */
  idleWarnTimeMS?: number;

  /**
  * How long a user can be idle before the session expires
  * @default 600_000 (10 minutes)
  */
  sessionExpireTimeMS?: number;

  /**
   * A callback to run when the user session expires
   */
  onSessionExpire: () => void;
};

export const useIdleSession = ({
  activityThrottleTimeMS = 1_000,
  idleWarnTimeMS = 30_000,
  sessionExpireTimeMS = 600_000,
  onSessionExpire,
}: UseIdleSessionProps) => {
  const [isIdle, setIsIdle] = useState(false);
  const latestActivity = useRef(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const trackActivity = () => {
    const now = Date.now();
    // Throttle updating the localStorage to once per activityThrottleTimeMS
    if ((now - latestActivity.current) < activityThrottleTimeMS) return;

    latestActivity.current = now;
  };

  /**
  * Ping the Daffy servers with an updated user activity time, to prevent/trigger other windows session timeout functionality
  *
  * @param idleForMS The number of miliseconds the user has been idle for
  * @returns Either the number of seconds the user has been inactive for, or the string 'logout' if the user should be logged out
  */
  const pingActivity = async (idleForMS: number = 0) => {
    try {
      // Daffy endpoint expects idleFor param in integer seconds
      const idleForSeconds = Math.floor(idleForMS / 1_000);
      const params = buildURLParamsString({ idleFor: idleForSeconds });
      const data = await ajaxFetch(`${routes.sessionPing}${params}`) as ('logout' | number);

      // Sync to server activity only when the server reports meaningfully more-recent
      // activity than the client's own view (e.g. from another browser tab). Without
      // this threshold, rounding echoes between client and server keep advancing
      // latestActivity forward and prevent idleForMS from ever crossing the warn/expire
      // thresholds.
      if (typeof data === 'number' && idleForSeconds - data > SERVER_SYNC_THRESHOLD_SECONDS) {
        const serverLatestActivity = Date.now() - (data * 1_000);
        latestActivity.current = Math.max(latestActivity.current, serverLatestActivity);
      }

      return data;
    } catch (err) {
      return 0;
    }
  };

  // RUN ON COMPONENT MOUNT
  useEffect(() => {
    // Guards against a stale checkActivity chain re-scheduling itself after the
    // effect has been cleaned up (e.g. sessionExpireTimeMS changing while a ping
    // is in flight).
    let isActive = true;

    const checkActivity = async () => {
      if (!isActive) return;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const now = Date.now();
      const idleForMS = Math.floor(now - latestActivity.current);
      const data = await pingActivity(idleForMS);
      if (!isActive) return;

      const shouldExpire = idleForMS >= sessionExpireTimeMS || data === 'logout';
      const shouldMarkIdle = idleForMS >= sessionExpireTimeMS - idleWarnTimeMS;

      // If idle for longer than session expire time, then expire user session and don't set another timeout
      if (shouldExpire) {
        onSessionExpire();
        return;
      }

      setIsIdle(shouldMarkIdle);

      const nextCheckActivityInterval = Math.min(
        600_000, // 10 min cap
        shouldMarkIdle
          ? sessionExpireTimeMS - idleForMS
          : sessionExpireTimeMS - idleWarnTimeMS - idleForMS,
      );

      timeoutRef.current = setTimeout(
        checkActivity,
        // Sanity check to make sure we aren't breaking JS timeout logic
        Math.min(nextCheckActivityInterval, SETTIMEOUT_MAXIMUM),
      );
    };

    const trackedEvents = ['mousemove', 'scroll', 'keyup', 'click'] as const;

    for (let i = 0; i < trackedEvents.length; i += 1) {
      document.addEventListener(trackedEvents[i], trackActivity);
    }

    checkActivity();

    // RUN ON COMPONENT WILL UNMOUNT
    return () => {
      isActive = false;
      clearTimeout(timeoutRef.current);
      for (let i = 0; i < trackedEvents.length; i += 1) {
        document.removeEventListener(trackedEvents[i], trackActivity);
      }
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on MOUNT, UNMOUNT, & when sessionExpireTimeMS updates
  [sessionExpireTimeMS]);

  return {
    isIdle, setIsIdle, pingActivity,
  };
};
